import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DatastoreCollection } from '@freelancer/datastore';
import {
  Publication,
  PublicationsCollection,
} from '@freelancer/datastore/collections';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, TextSize } from '@freelancer/ui/text';
import * as Rx from 'rxjs';

@Component({
  selector: 'app-user-profile-publications-view',
  template: `
    <fl-bit class="Title">
      <fl-heading
        *ngIf="publication.title"
        class="Heading"
        [size]="TextSize.SMALL"
        [headingType]="HeadingType.H2"
        [flMarginBottom]="Margin.XSMALL"
      >
        {{ publication.title }}
      </fl-heading>

      <app-user-profile-editable-ui-drop-actions
        *ngIf="editable"
        flTrackingLabel="UserProfilePublicationView"
        (delete)="handleActionDelete()"
        (edit)="handleActionEdit()"
      ></app-user-profile-editable-ui-drop-actions>
    </fl-bit>

    <fl-text
      *ngIf="publication.publisher"
      [color]="FontColor.MID"
      [flMarginBottom]="Margin.XSMALL"
    >
      {{ publication.publisher }}
    </fl-text>

    <fl-text
      class="Summary"
      *ngIf="publication.summary"
      [flMarginBottom]="Margin.SMALL"
    >
      {{ publication.summary }}
    </fl-text>

    <fl-banner-alert
      *ngIf="isError$ | flAsync"
      i18n="Error message for publication action"
      [type]="BannerAlertType.ERROR"
    >
      Oops, something went wrong when saving your publication item. Please try
      again.
    </fl-banner-alert>

    <app-user-profile-editable-ui-confirmation
      *ngIf="needsDeleteConfirmation$ | flAsync"
      title="Delete Publication"
      i18n-title="Delete publication confirmation title"
      body="Are you sure you want to delete this publication?"
      i18n-body="Delete publication confirmation body"
      flTrackingLabel="UserProfilePublicationViewDelete"
      [isBusy]="isBusy$ | flAsync"
      (confirm)="handleConfirmDeletePublication()"
      (cancel)="handleCancelDeletePublication()"
    ></app-user-profile-editable-ui-confirmation>
  `,
  styleUrls: ['./user-profile-publications-view.component.scss'],
})
export class UserProfilePublicationsViewComponent {
  BannerAlertType = BannerAlertType;
  FontColor = FontColor;
  TextSize = TextSize;
  HeadingType = HeadingType;
  Margin = Margin;

  @Input() editable: boolean;
  @Input() publication: Publication;
  @Input() publicationsCollection: DatastoreCollection<PublicationsCollection>;
  @Output() edit = new EventEmitter<Publication>();

  private _needsDeleteConfirmation$ = new Rx.BehaviorSubject<boolean>(false);
  needsDeleteConfirmation$ = this._needsDeleteConfirmation$.asObservable();

  private _isBusy$ = new Rx.BehaviorSubject<boolean>(false);
  isBusy$ = this._isBusy$.asObservable();

  private _isError$ = new Rx.BehaviorSubject<boolean>(false);
  isError$ = this._isError$.asObservable();

  handleActionEdit(): void {
    this.edit.emit(this.publication);
  }

  handleActionDelete(): void {
    this._needsDeleteConfirmation$.next(true);
  }

  handleConfirmDeletePublication(): void {
    this._isBusy$.next(true);
    this.publicationsCollection.remove(this.publication.id).then(response => {
      this._needsDeleteConfirmation$.next(false);
      this._isBusy$.next(false);

      if (response.status === 'error') {
        this._isError$.next(true);
      }
    });
  }

  handleCancelDeletePublication(): void {
    this._needsDeleteConfirmation$.next(false);
  }
}
